import {
  RecipientInfo,
  PrepaidSubPurchaseDetails,
} from "@bluebottlecoffee/design-system/components";
import { Product } from "@bluebottlecoffee/design-system/components/lib/types";
import { createMachine, assign } from "xstate";

export type Step = "stepOne" | "stepTwo" | "stepThree";

type StepOneData = PrepaidSubPurchaseDetails;

type StepTwoData = RecipientInfo;

// We don't currently need anything in StepThree, because it will actually just
// utilize context from other steps, but we need this type to maintain the
// ContextFields data structure below
type StepThreeData = {};

type ContextFields = {
  [Property in Step]: Property extends "stepOne"
    ? StepOneData
    : Property extends "stepTwo"
      ? StepTwoData
      : Property extends "stepThree"
        ? StepThreeData
        : never;
};

type Context = {
  data: ContextFields;
};

type Event = {
  type: string;
  value: Partial<ContextFields>;
};

type States = {
  value: Step;
  context: Context;
};

type GiftSubCheckoutFormStateMachine = {
  ctas: {
    [Property in Step]: string;
  };
  disclaimers?: {
    [Property in Step]?: string;
  };
  id: string;
  product: Product;
};

export const giftableSubscriptionOptions = (product: Product) =>
  product.subscriptionOptions?.filter((subOption) => subOption?.isGiftable);

const initialInterval = (product: Product) =>
  product.subscription.intervals.length - 1;

export const giftSubCheckoutFormStateMachine = ({
  ctas,
  disclaimers,
  id,
  product,
}: GiftSubCheckoutFormStateMachine) =>
  createMachine<Context, Event, States>(
    {
      id,
      initial: "stepOne",
      context: {
        data: {
          stepOne: {
            computedSubtotal: "",
            duration: {
              _id: product.subscription.durations[0]._id,
              presentation: product.subscription.durations[0].presentation,
            },
            installmentCount: 0,
            interval: {
              _id: product.subscription.intervals[initialInterval(product)]._id,
              length:
                product.subscription.intervals[initialInterval(product)].length,
              presentation:
                product.subscription.intervals[initialInterval(product)]
                  .presentation,
              unit: product.subscription.intervals[initialInterval(product)]
                .unit,
            },
            lineItems: [
              {
                sku: giftableSubscriptionOptions(product)?.[0].item.sku,
                quantity:
                  giftableSubscriptionOptions(product)?.[0].quantityLabel
                    .quantity,
              },
            ],
            subscriptionOptionId: giftableSubscriptionOptions(product)?.[0]._id,
          },
          stepTwo: {
            address: {
              name: "",
              street: "",
              extended: "",
              locality: "",
              district: "",
              postalCode: "",
              countryCode: "",
              default: false,
            },
            email: "",
            giftNote: "",
          },
          stepThree: {},
        },
      },
      states: {
        stepOne: {
          on: {
            NEXT: { target: "stepTwo" },
            NEXT_TO_REVIEW: { target: "stepThree" },
            UPDATE: { actions: "update" },
          },
          meta: {
            index: 1,
            disclaimer: disclaimers?.stepOne,
            nextButtonText: ctas.stepOne,
          },
        },
        stepTwo: {
          on: {
            NEXT: { target: "stepThree" },
            PREV: { target: "stepOne" },
            UPDATE: { actions: "update" },
          },
          meta: {
            index: 2,
            disclaimer: disclaimers?.stepTwo,
            nextButtonText: ctas.stepTwo,
          },
        },
        stepThree: {
          on: {
            PREV: { target: "stepTwo" },
            PREV_TO_DETAILS: { target: "stepOne" },
          },
          meta: {
            index: 3,
            disclaimer: disclaimers?.stepThree,
            nextButtonText: ctas.stepThree,
          },
        },
      },
      predictableActionArguments: true,
    },
    {
      actions: {
        update: assign({
          data: (context, event) => ({ ...context.data, ...event.value }),
        }),
      },
    },
  );
